/* eslint-disable @typescript-eslint/explicit-function-return-type */
import type {GetPerksParamsADto} from '@cohort/admin-schemas/perk';
import type {
  PerkAnalyticsParamsADto,
  PerksAnalyticsOverviewParamsADto,
} from '@cohort/admin-schemas/perkAnalytics';
import type {QueryOptions} from '@cohort/merchants/hooks/api/Query';
import {useCohortQuery} from '@cohort/merchants/hooks/api/Query';
import {
  getPerk,
  getPerkAnalytics,
  getPerks,
  getPerksAnalyticsOverviews,
} from '@cohort/merchants/lib/api/Perks';

export const perksKeys = {
  perks: ['perks'] as const,
  list: (merchantId: string) => [...perksKeys.perks, merchantId, 'list'] as const,
  listWithFilters: (merchantId: string, params: GetPerksParamsADto) =>
    [...perksKeys.list(merchantId), params] as const,
  analytics: (merchantId: string, params: PerksAnalyticsOverviewParamsADto) =>
    [...perksKeys.list(merchantId), 'analytics', params] as const,
  getById: (merchantId: string, perkId: string) =>
    [...perksKeys.perks, merchantId, perkId] as const,
  analyticsById: (merchantId: string, perkId: string, params: PerkAnalyticsParamsADto) =>
    [...perksKeys.perks, merchantId, perkId, 'analytics', params] as const,
  countById: (merchantId: string, perkId: string) =>
    [...perksKeys.getById(merchantId, perkId), 'count'] as const,
};

export const usePerk = (merchantId: string, perkId: string, options?: QueryOptions) =>
  useCohortQuery({
    queryKey: perksKeys.getById(merchantId, perkId),
    queryFn: async () => getPerk(merchantId, perkId),
    ...options,
  });

export const usePerks = (merchantId: string, params: GetPerksParamsADto, options?: QueryOptions) =>
  useCohortQuery({
    queryKey: perksKeys.listWithFilters(merchantId, params),
    queryFn: async () => getPerks(merchantId, params),
    ...options,
  });

export const usePerkAnalytics = (
  merchantId: string,
  params: PerksAnalyticsOverviewParamsADto,
  options?: QueryOptions
) =>
  useCohortQuery({
    queryKey: perksKeys.analytics(merchantId, params),
    queryFn: async () => getPerksAnalyticsOverviews(merchantId, params),
    ...options,
  });

export const usePerkAnalyticsById = (
  merchantId: string,
  perkId: string,
  params: PerkAnalyticsParamsADto
) =>
  useCohortQuery({
    queryKey: perksKeys.analyticsById(merchantId, perkId, params),
    queryFn: async () => getPerkAnalytics(merchantId, perkId, params),
  });

export const usePerkUsageCount = (merchantId: string, perkId: string, options?: QueryOptions) =>
  useCohortQuery({
    queryKey: perksKeys.countById(merchantId, perkId),
    queryFn: async () =>
      Promise.all([
        getPerkAnalytics(merchantId, perkId, {
          statuses: ['used'],
          page: 1,
          pageSize: 1,
        }),
        getPerkAnalytics(merchantId, perkId, {
          statuses: [],
          page: 1,
          pageSize: 1,
        }),
      ]),
    select: ([used, all]) => ({
      used: used[0].total,
      total: all[0].total,
    }),
    ...options,
  });
